import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LeadDecisionSummary } from "../../components/product/LeadDecisionSummary.jsx";
import { UnderstandingBlock, keptAliases } from "./UnderstandingBlock.jsx";
import { AxonIcon } from "../../icons/AxonIcon.jsx";
import { countBucket, durationBucket, recordUserAction } from "../analytics/tracking.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import {
  fetchLeadProfile,
  previewLeadProfile,
  saveLeadProfile,
  saveOnboardingExampleDecisions,
} from "../leads/api.js";
import { editableLeadProfile, leadProfilePayload, textFields } from "../leads/leadProfileState.js";
import { activateOnboarding, saveProfile } from "../profile/api.js";
import { useWorkspaceTimeZone } from "../profile/WorkspaceTimeZone.jsx";
import { editableProfile, profilePayload } from "../profile/profileState.js";
import { AxonGuide } from "./AxonGuide.jsx";
import {
  ONBOARDING_CATALOGUE_VERSION,
  ONBOARDING_FAMILIES,
  projectOnboardingSelection,
} from "./onboardingCatalogue.js";
import { playOnboardingSignal } from "./onboardingSignal.js";

const POSTPONED_STORAGE_KEY = "axon-onboarding-postponed-v1";
const STEPS = ["Что ищем", "Примеры"];
const NOT_FIT_REASONS = [
  ["wrong_specialty", "Другая специализация"],
  ["budget_too_low", "Маленький бюджет"],
  ["not_real_order", "Это не заказ"],
  ["other", "Другое"],
];

function chipOnboardingEnabled() {
  return import.meta.env.VITE_AXON_CHIP_ONBOARDING_ENABLED === "true";
}

function wasPostponed() {
  try {
    return globalThis.sessionStorage?.getItem(POSTPONED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function rememberPostponed(value) {
  try {
    if (value) globalThis.sessionStorage?.setItem(POSTPONED_STORAGE_KEY, "true");
    else globalThis.sessionStorage?.removeItem(POSTPONED_STORAGE_KEY);
  } catch {
    // Серверный onboardingStep остаётся источником истины.
  }
}

function formatBudget(amount, currency) {
  if (!amount || !currency) return null;
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function freshnessLabel(value) {
  const published = new Date(value);
  if (Number.isNaN(published.getTime())) return "недавно";
  const hours = Math.max(0, Math.round((Date.now() - published.getTime()) / 3_600_000));
  if (hours < 24) return "сегодня";
  if (hours < 48) return "вчера";
  return `${Math.ceil(hours / 24)} дн. назад`;
}

function profileWithDesiredWork(profile, desiredWork, authName, niche = "") {
  // Поисковая фраза и представление клиенту — разные вещи. Человек пишет
  // «бэкэнд плюс создание лэндингов», чтобы НАЙТИ заказы, а клиент читал это
  // на визитке как его должность. Берём нишу, понятую ИИ: она нормализована
  // и звучит так, как назвал бы услугу сам заказчик. Ниша перебивает роль,
  // сохранённую черновиком до того, как мы фразу поняли.
  const публично = niche.trim() || profile.professionalRole || desiredWork;
  return {
    ...profile,
    displayName: profile.displayName || authName || "Фрилансер",
    professionalRole: публично,
    services: [{
      title: niche.trim() || desiredWork,
      description: profile.services[0]?.description || "",
      active: true,
    }],
  };
}

function profileWithProjection(profile, projection, authName) {
  return {
    ...profile,
    displayName: profile.displayName || authName || "Фрилансер",
    professionalRole: projection.professionalRole,
    services: [projection.service],
  };
}

function useForegroundActiveDuration(running) {
  const accumulatedRef = useRef(0);
  const startedAtRef = useRef(null);

  useEffect(() => {
    const pause = () => {
      if (startedAtRef.current === null) return;
      accumulatedRef.current += performance.now() - startedAtRef.current;
      startedAtRef.current = null;
    };
    const resume = () => {
      if (!running || document.visibilityState === "hidden" || startedAtRef.current !== null) return;
      startedAtRef.current = performance.now();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") pause();
      else resume();
    };

    if (running) resume();
    else pause();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      pause();
    };
  }, [running]);

  return () => accumulatedRef.current + (
    startedAtRef.current === null ? 0 : performance.now() - startedAtRef.current
  );
}

function toggleId(ids, id) {
  return ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];
}

export function ConversationalOnboarding({
  initialProfile,
  onActivated,
  enableChipOnboarding = chipOnboardingEnabled(),
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { setTimeZone } = useWorkspaceTimeZone();
  const [profile, setProfile] = useState(() => editableProfile(initialProfile));
  const initialDesiredWork = initialProfile.services?.[0]?.title || "";
  const [leadProfile, setLeadProfile] = useState(() => editableLeadProfile({
    rules: { serviceTerms: initialDesiredWork ? [initialDesiredWork] : [] },
  }));
  const [fields, setFields] = useState(() => textFields(editableLeadProfile({
    rules: { serviceTerms: initialDesiredWork ? [initialDesiredWork] : [] },
  })));
  const [step, setStep] = useState(() => (
    initialProfile.onboardingStep >= 2 && initialDesiredWork ? 2 : 1
  ));
  const [mode, setMode] = useState(() => (wasPostponed() ? "postponed" : "welcome"));
  const [status, setStatus] = useState("ready");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  // Какие синонимы человек снял. Его правка важнее мнения модели.
  const [removedAliases, setRemovedAliases] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [pendingNotFitId, setPendingNotFitId] = useState(null);
  const [activation, setActivation] = useState(null);
  const [selectionStage, setSelectionStage] = useState("family");
  const [selection, setSelection] = useState({
    familyId: null,
    modeId: null,
    deliverableIds: [],
    exclusionIds: [],
  });
  const [customInputOpen, setCustomInputOpen] = useState(false);
  const trackedViewRef = useRef(false);
  const activeDuration = useForegroundActiveDuration(mode === "editing");

  useEffect(() => {
    if (initialProfile.onboardingComplete || trackedViewRef.current) return;
    trackedViewRef.current = true;
    void recordUserAction(auth, {
      name: "onboarding.dialog_viewed",
      entityType: "onboarding",
      entityId: "onboarding",
      step,
    });
  }, [auth, initialProfile.onboardingComplete, step]);

  useEffect(() => {
    if (initialProfile.onboardingComplete) return undefined;
    let active = true;
    void auth.getIdToken().then(async (token) => {
      if (!token) return;
      try {
        const loaded = editableLeadProfile(await fetchLeadProfile(token));
        if (!active) return;
        const next = loaded.rules.serviceTerms.length || !initialDesiredWork
          ? loaded
          : { ...loaded, rules: { ...loaded.rules, serviceTerms: [initialDesiredWork] } };
        setLeadProfile(next);
        setFields(textFields(next));
      } catch {
        // Первый вопрос остаётся доступным даже при временной ошибке загрузки черновика.
      }
    });
    return () => { active = false; };
  }, [auth, initialDesiredWork, initialProfile.onboardingComplete]);

  const selectedFamily = useMemo(
    () => ONBOARDING_FAMILIES.find((item) => item.id === selection.familyId) || null,
    [selection.familyId],
  );
  const selectedMode = useMemo(
    () => selectedFamily?.modes.find((item) => item.id === selection.modeId) || null,
    [selectedFamily, selection.modeId],
  );
  const projection = useMemo(() => {
    if (!selectedMode) return null;
    try {
      return projectOnboardingSelection(selection);
    } catch {
      return null;
    }
  }, [selectedMode, selection]);
  const examples = useMemo(() => (preview?.items || []).slice(0, 3), [preview]);
  const decidedExamples = examples.filter((item) => decisions[item.id]).length;
  const currentExample = examples.find((item) => !decisions[item.id]) || null;
  const allExamplesDecided = examples.length === 0
    ? Boolean(preview)
    : examples.every((item) => decisions[item.id]);

  if (initialProfile.onboardingComplete) return null;

  function updateField(field, value) {
    setFields((current) => ({ ...current, [field]: value }));
    setError("");
  }

  function trackOnboarding(name, extra = {}) {
    void recordUserAction(auth, {
      name,
      entityType: "onboarding",
      entityId: "onboarding",
      ...extra,
    });
  }

  function start() {
    playOnboardingSignal();
    rememberPostponed(false);
    setMode("editing");
    setError("");
    trackOnboarding("onboarding.started", { step });
    if (step === 2 && !preview) void loadExamples();
  }

  function postpone() {
    rememberPostponed(true);
    setMode("postponed");
    trackOnboarding("onboarding.postponed", { step, result: "postponed" });
  }

  function chooseFamily(family) {
    setSelection({ familyId: family.id, modeId: null, deliverableIds: [], exclusionIds: [] });
    setSelectionStage("mode");
    setError("");
    trackOnboarding("onboarding.family_selected", { kind: family.id });
  }

  function chooseMode(nextMode) {
    setSelection((current) => ({
      ...current,
      modeId: nextMode.id,
      deliverableIds: nextMode.deliverables.map((item) => item.id),
      exclusionIds: nextMode.exclusions.map((item) => item.id),
    }));
    setSelectionStage("deliverables");
    setError("");
    trackOnboarding("onboarding.mode_selected", {
      kind: `${selection.familyId}:${nextMode.id}`,
    });
  }

  function openCustomInput() {
    setCustomInputOpen(true);
    setSelectionStage("custom");
    setError("");
    trackOnboarding("onboarding.custom_input_opened");
  }

  function confirmDeliverables() {
    if (!selection.deliverableIds.length) {
      setError("Выберите хотя бы один результат, который получает клиент.");
      return;
    }
    setSelectionStage("exclusions");
    setError("");
    trackOnboarding("onboarding.capabilities_confirmed", {
      countBucket: countBucket(selection.deliverableIds.length),
    });
  }

  function confirmExclusions() {
    setSelectionStage("summary");
    setError("");
    trackOnboarding("onboarding.exclusions_confirmed", {
      countBucket: countBucket(selection.exclusionIds.length),
    });
  }

  async function loadExamples(payload) {
    setStatus("previewing");
    setError("");
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      const request = payload || leadProfilePayload(leadProfile, fields, "draft", null, true);
      const result = await previewLeadProfile(token, request);
      setPreview(result);
      setRemovedAliases([]);
      setDecisions({});
      setPendingNotFitId(null);
      setStatus("ready");
    } catch (previewError) {
      setError(previewError.message || "Не получилось показать примеры. Попробуйте ещё раз.");
      setStatus("ready");
    }
  }

  async function saveDesiredWork(nextProjection = null) {
    const desiredWork = nextProjection?.serviceTerm || fields.serviceTerms.trim();
    if (!desiredWork) {
      setError("Напишите, какие заказы вы хотите получать.");
      return;
    }
    setStatus("saving");
    setError("");
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      const nextProfile = nextProjection
        ? profileWithProjection(profile, nextProjection, auth.user?.displayName)
        : profileWithDesiredWork(profile, desiredWork, auth.user?.displayName);
      const nextLeadProfile = {
        ...leadProfile,
        rules: { ...leadProfile.rules, serviceTerms: [desiredWork] },
      };
      const nextFields = { ...fields, serviceTerms: desiredWork };
      const searchPayload = leadProfilePayload(nextLeadProfile, nextFields, "draft", null, true);
      const [savedSearch, savedProfile] = await Promise.all([
        saveLeadProfile(token, searchPayload).then(editableLeadProfile),
        saveProfile(token, profilePayload(nextProfile, {
          onboardingStep: 3,
          completeOnboarding: false,
        })),
      ]);
      setProfile(editableProfile(savedProfile));
      setTimeZone(savedProfile.timezone);
      setLeadProfile(savedSearch);
      setFields(textFields(savedSearch));
      setStep(2);
      trackOnboarding("onboarding.step_saved", { step: 1 });
      if (nextProjection) trackOnboarding("onboarding.summary_confirmed");
      await loadExamples(searchPayload);
    } catch (saveError) {
      setError(saveError.message || "Не получилось сохранить ответ.");
      setStatus("ready");
    }
  }

  function decideExample(verdict, reasonCode = null) {
    if (!currentExample) return;
    if (verdict === "not_fit" && !reasonCode) {
      setPendingNotFitId(currentExample.id);
      return;
    }
    const decision = { verdict, ...(reasonCode ? { reasonCode } : {}) };
    setDecisions((current) => ({ ...current, [currentExample.id]: decision }));
    setPendingNotFitId(null);
    trackOnboarding("onboarding.example_decided", {
      verdict,
      ...(reasonCode ? { reasonCode } : {}),
    });
  }

  async function activate() {
    if (!allExamplesDecided || status !== "ready") return;
    setStatus("activating");
    setError("");
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      // Синонимы, понятые ИИ и оставленные человеком, сохраняем вместе с
      // поиском: без них ниша снова ищется одной формулировкой.
      const aliases = keptAliases(preview?.understanding, removedAliases);
      let activationLeadProfile = leadProfile;
      const savedAliases = leadProfile.rules.serviceAliases || [];
      const aliasesChanged = aliases.length !== savedAliases.length
        || aliases.some((alias, index) => alias !== savedAliases[index]);
      if (aliasesChanged && leadProfile.version >= 1) {
        const withAliases = leadProfilePayload(leadProfile, fields, "draft", aliases, true);
        const saved = await saveLeadProfile(token, withAliases).then(editableLeadProfile);
        setLeadProfile(saved);
        activationLeadProfile = saved;
      }
      if (examples.length) {
        if (!activationLeadProfile.version || activationLeadProfile.version < 1) {
          throw new Error("Настройка поиска ещё сохраняется. Попробуйте ещё раз.");
        }
        await saveOnboardingExampleDecisions(token, {
          profileVersion: activationLeadProfile.version,
          catalogueVersion: ONBOARDING_CATALOGUE_VERSION,
          items: examples.map((item) => ({ leadId: item.id, ...decisions[item.id] })),
        });
      }
      const desiredWork = fields.serviceTerms.trim() || profile.services[0]?.title || profile.professionalRole;
      const completedProfile = projection && !customInputOpen
        ? profileWithProjection(profile, projection, auth.user?.displayName)
        : profileWithDesiredWork(
            profile,
            desiredWork,
            auth.user?.displayName,
            preview?.understanding?.niche || "",
          );
      const result = await activateOnboarding(token, profilePayload(completedProfile, {
        onboardingStep: 3,
        completeOnboarding: true,
      }));
      const assigned = result.assignment?.created || 0;
      const resultKind = result.assignmentStatus === "pending"
        ? "pending"
        : assigned > 0 ? "success" : "zero_matches";
      setProfile(editableProfile(result.profile));
      setTimeZone(result.profile.timezone);
      setActivation({ assigned, result: resultKind });
      setMode("success");
      setStatus("ready");
      trackOnboarding("onboarding.activated", {
        result: resultKind,
        countBucket: countBucket(assigned),
        durationBucket: durationBucket(activeDuration()),
      });
      await onActivated?.();
    } catch (activationError) {
      setError(activationError.message || "Не получилось включить поиск.");
      setStatus("ready");
      trackOnboarding("onboarding.activation_failed", { result: "error" });
    }
  }

  function resetSelection() {
    setSelection({ familyId: null, modeId: null, deliverableIds: [], exclusionIds: [] });
    setSelectionStage("family");
    setCustomInputOpen(false);
    setError("");
  }

  const greetingName = profile.displayName || auth.user?.displayName;
  const guideState = error
    ? "notice"
    : mode === "success"
      ? "success"
      : mode === "editing"
        ? step === 1 ? "choose" : "review"
        : "welcome";
  const reviewHeading = status === "previewing" && !preview
    ? "Подбираю примеры"
    : error && !preview
      ? "Попробуем ещё раз"
      : currentExample
        ? "Этот заказ вам подходит?"
        : "Можно включать поиск";

  return (
    <section className={`assistant-onboarding is-${mode}`} aria-label="Настройка через рабочий диалог">
      <div className="assistant-message assistant-message--axon assistant-onboarding__message">
        <div className="assistant-message__author type-meta">Axon</div>
        <div className="assistant-onboarding__content">
          {mode === "postponed" ? (
            <button type="button" className="assistant-onboarding__resume" onClick={start}>
              <AxonIcon name="conversation" size={20} active />
              <span><strong>Закончить настройку</strong><small>Этап {step} из 2 · около минуты</small></span>
              <span aria-hidden="true">→</span>
            </button>
          ) : null}

          {mode !== "postponed" ? (
            <div className="assistant-onboarding__stage">
              <AxonGuide state={guideState} className="assistant-onboarding__guide" />
              <div className="assistant-onboarding__panel">
                {mode === "welcome" ? (
                  <div className="assistant-onboarding__welcome">
                    <span className="assistant-onboarding__signal" aria-hidden="true" />
                    <p className="type-meta">Первый рабочий диалог</p>
                    <h3>{greetingName ? `Привет, ${greetingName}. За минуту настроим подходящие заказы?` : "Привет. За минуту настроим подходящие заказы?"}</h3>
                    <p>Выберем вашу работу и проверим три настоящих примера. Длинной анкеты не будет.</p>
                    <div className="assistant-onboarding__welcome-actions">
                      <button type="button" className="is-primary" onClick={start}>Настроить лиды</button>
                      <button type="button" className="is-quiet" onClick={postpone}>Позже</button>
                    </div>
                  </div>
                ) : null}

                {mode === "editing" ? (
                  <form className="assistant-onboarding__form" onSubmit={(event) => event.preventDefault()}>
                    <OnboardingProgress step={step} />
                    {step === 1 && enableChipOnboarding ? (
                      <ChipProfileStep
                        stage={selectionStage}
                        family={selectedFamily}
                        mode={selectedMode}
                        selection={selection}
                        projection={projection}
                        fields={fields}
                        status={status}
                        onChooseFamily={chooseFamily}
                        onChooseMode={chooseMode}
                        onToggleDeliverable={(id) => setSelection((current) => ({
                          ...current,
                          deliverableIds: toggleId(current.deliverableIds, id),
                        }))}
                        onToggleExclusion={(id) => setSelection((current) => ({
                          ...current,
                          exclusionIds: toggleId(current.exclusionIds, id),
                        }))}
                        onConfirmDeliverables={confirmDeliverables}
                        onConfirmExclusions={confirmExclusions}
                        onOpenCustom={openCustomInput}
                        onCustomChange={(value) => updateField("serviceTerms", value)}
                        onSaveCustom={() => void saveDesiredWork()}
                        onConfirmSummary={() => void saveDesiredWork(projection)}
                        onBack={(nextStage) => { setError(""); setSelectionStage(nextStage); }}
                        onReset={resetSelection}
                        onPostpone={postpone}
                      />
                    ) : null}

                    {step === 1 && !enableChipOnboarding ? (
                      <LegacyProfileStep
                        fields={fields}
                        status={status}
                        onChange={(value) => updateField("serviceTerms", value)}
                        onSave={() => void saveDesiredWork()}
                        onPostpone={postpone}
                      />
                    ) : null}

                    {step === 2 ? (
                      <ExampleReviewStep
                        status={status}
                        preview={preview}
                        examples={examples}
                        currentExample={currentExample}
                        decidedExamples={decidedExamples}
                        allExamplesDecided={allExamplesDecided}
                        reviewHeading={reviewHeading}
                        pendingNotFitId={pendingNotFitId}
                        onDecision={decideExample}
                        removedAliases={removedAliases}
                        onToggleAlias={(alias) =>
                          setRemovedAliases((prev) =>
                            prev.includes(alias)
                              ? prev.filter((item) => item !== alias)
                              : [...prev, alias],
                          )
                        }
                      />
                    ) : null}

                    {error ? <p className="assistant-onboarding__error" role="alert">{error}</p> : null}
                    {step === 2 ? (
                      <div className="assistant-onboarding__actions">
                        <button type="button" className="is-quiet" onClick={() => { setError(""); setStep(1); resetSelection(); }}>Изменить, что ищем</button>
                        {allExamplesDecided && examples.length ? (
                          <button type="button" className="is-quiet" onClick={() => { setDecisions({}); setPendingNotFitId(null); }}>Ответить заново</button>
                        ) : null}
                        {error && !preview ? (
                          <button type="button" className="is-primary" disabled={status !== "ready"} onClick={() => void loadExamples()}>Повторить</button>
                        ) : null}
                        {allExamplesDecided ? (
                          <button type="button" className="is-primary" disabled={status !== "ready"} onClick={() => void activate()}>
                            {status === "activating" ? "Включаю…" : "Включить поиск"}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </form>
                ) : null}

                {mode === "success" ? (
                  <div className="assistant-onboarding__success" role="status">
                    <p className="type-meta">Готово</p>
                    <h3>Поиск включён</h3>
                    <p>{activation?.result === "pending"
                      ? "Настройки сохранены. Поиск продолжится в фоне."
                      : activation?.assigned
                        ? `Найдено заказов для проверки: ${activation.assigned}.`
                        : "Подходящих заказов пока нет. Сообщу, когда такой появится."}</p>
                    <div className="assistant-onboarding__welcome-actions">
                      <button type="button" className="is-primary" onClick={() => navigate("/leads")}>Проверить лиды</button>
                      <button type="button" className="is-quiet" onClick={() => navigate("/leads/settings")}>Уточнить поиск</button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ChipProfileStep({
  stage,
  family,
  mode,
  selection,
  projection,
  fields,
  status,
  onChooseFamily,
  onChooseMode,
  onToggleDeliverable,
  onToggleExclusion,
  onConfirmDeliverables,
  onConfirmExclusions,
  onOpenCustom,
  onCustomChange,
  onSaveCustom,
  onConfirmSummary,
  onBack,
  onReset,
  onPostpone,
}) {
  if (stage === "custom") {
    return (
      <>
        <p className="type-meta">Своими словами</p>
        <h3>Какие заказы вам нужны?</h3>
        <label>
          <span>Коротко опишите одну основную услугу</span>
          <textarea
            autoFocus
            rows="3"
            value={fields.serviceTerms}
            maxLength="120"
            placeholder="Например: реставрация старых фотографий"
            onChange={(event) => onCustomChange(event.target.value)}
          />
        </label>
        <div className="assistant-onboarding__actions">
          <button type="button" className="is-quiet" onClick={onReset}>Назад к вариантам</button>
          <button type="button" className="is-primary" disabled={status !== "ready"} onClick={onSaveCustom}>
            {status === "saving" ? "Сохраняю…" : "Показать примеры"}
          </button>
        </div>
      </>
    );
  }

  if (stage === "mode") {
    return (
      <>
        <p className="type-meta">{family?.label}</p>
        <h3>Что именно вы делаете?</h3>
        <ChipGroup label="Выберите основную специализацию">
          {(family?.modes || []).map((item) => (
            <button key={item.id} type="button" onClick={() => onChooseMode(item)}>{item.label}</button>
          ))}
        </ChipGroup>
        <div className="assistant-onboarding__actions">
          <button type="button" className="is-quiet" onClick={() => onBack("family")}>Назад</button>
          <button type="button" className="is-quiet" onClick={onOpenCustom}>Не нашли свой вариант</button>
        </div>
      </>
    );
  }

  if (stage === "deliverables") {
    return (
      <>
        <p className="type-meta">{family?.label} · {mode?.label}</p>
        <h3>Что получает клиент?</h3>
        <p>Мы уже отметили типичный набор. Уберите лишнее одним нажатием.</p>
        <ChipGroup label="Результаты работы" multiple>
          {(mode?.deliverables || []).map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={selection.deliverableIds.includes(item.id)}
              onClick={() => onToggleDeliverable(item.id)}
            >
              {item.label}
            </button>
          ))}
        </ChipGroup>
        <div className="assistant-onboarding__actions">
          <button type="button" className="is-quiet" onClick={() => onBack("mode")}>Назад</button>
          <button type="button" className="is-primary" onClick={onConfirmDeliverables}>Продолжить</button>
        </div>
      </>
    );
  }

  if (stage === "exclusions") {
    return (
      <>
        <p className="type-meta">Граница специализации</p>
        <h3>Что лучше сразу не показывать?</h3>
        <p>Соседние задачи уже отмечены. Оставьте только то, что вы точно не берёте.</p>
        <ChipGroup label="Не показывать" multiple>
          {(mode?.exclusions || []).map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={selection.exclusionIds.includes(item.id)}
              onClick={() => onToggleExclusion(item.id)}
            >
              {item.label}
            </button>
          ))}
        </ChipGroup>
        <div className="assistant-onboarding__actions">
          <button type="button" className="is-quiet" onClick={() => onBack("deliverables")}>Назад</button>
          <button type="button" className="is-primary" onClick={onConfirmExclusions}>Собрать профиль</button>
        </div>
      </>
    );
  }

  if (stage === "summary") {
    return (
      <>
        <p className="type-meta">Проверьте одним взглядом</p>
        <h3>{projection?.service.title}</h3>
        <div className="assistant-onboarding__summary">
          <p>{projection?.service.description}</p>
        </div>
        <div className="assistant-onboarding__actions">
          <button type="button" className="is-quiet" onClick={() => onBack("deliverables")}>Изменить</button>
          <button type="button" className="is-primary" disabled={!projection || status !== "ready"} onClick={onConfirmSummary}>
            {status === "saving" ? "Сохраняю…" : "Всё верно"}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="type-meta">Без анкеты и клавиатуры</p>
      <h3>Что вы делаете для клиентов?</h3>
      <ChipGroup label="Выберите направление">
        {ONBOARDING_FAMILIES.map((item) => (
          <button key={item.id} type="button" onClick={() => onChooseFamily(item)}>{item.label}</button>
        ))}
      </ChipGroup>
      <div className="assistant-onboarding__actions">
        <button type="button" className="is-quiet" onClick={onPostpone}>Позже</button>
        <button type="button" className="is-quiet" onClick={onOpenCustom}>Не нашли свой вариант</button>
      </div>
    </>
  );
}

function ChipGroup({ label, multiple = false, children }) {
  return (
    <div
      className="assistant-onboarding__chip-grid"
      role="group"
      aria-label={label}
      data-selection={multiple ? "multiple" : "single"}
    >
      {children}
    </div>
  );
}

function LegacyProfileStep({ fields, status, onChange, onSave, onPostpone }) {
  return (
    <>
      <p className="type-meta">Один обязательный ответ</p>
      <h3>Что вы хотите делать?</h3>
      <label>
        <span>Какие заказы вы хотите получать?</span>
        <textarea
          autoFocus
          rows="3"
          value={fields.serviceTerms}
          maxLength="120"
          placeholder="Например: лендинги на Tilda"
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
      <div className="assistant-onboarding__actions">
        <button type="button" className="is-quiet" onClick={onPostpone}>Позже</button>
        <button type="button" className="is-primary" disabled={status !== "ready"} onClick={onSave}>
          {status === "saving" ? "Сохраняю…" : "Показать примеры"}
        </button>
      </div>
    </>
  );
}

function ExampleReviewStep({
  status,
  preview,
  examples,
  currentExample,
  decidedExamples,
  allExamplesDecided,
  reviewHeading,
  pendingNotFitId,
  onDecision,
  removedAliases = [],
  onToggleAlias,
}) {
  return (
    <>
      <UnderstandingBlock
        understanding={preview?.understanding}
        removed={removedAliases}
        onToggle={onToggleAlias}
        plan={preview?.plan || "free"}
      />
      <p className="type-meta">
        {currentExample
          ? `Пример ${decidedExamples + 1} из ${examples.length}`
          : status === "previewing" && !preview
            ? "Ищем свежие примеры"
            : preview && examples.length === 0
              ? "Примеров пока нет"
              : "Примеры проверены"}
      </p>
      <h3>{reviewHeading}</h3>
      {status === "previewing" && !preview ? <p role="status">Подбираю примеры…</p> : null}
      {preview && examples.length === 0 ? (
        <p className="assistant-onboarding__hint">Свежих примеров пока нет. Поиск всё равно можно включить — новые заказы появятся в «Лидах».</p>
      ) : null}
      {currentExample ? (
        <ol className="assistant-onboarding__examples">
          <li key={currentExample.id}>
            <LeadDecisionSummary
              headingLevel={4}
              title={currentExample.summary || currentExample.excerpt}
              excerpt={currentExample.excerpt}
              href={currentExample.canonicalUrl}
              source={currentExample.sourceName}
              freshness={freshnessLabel(currentExample.publishedAt)}
              budget={formatBudget(currentExample.budgetAmount, currentExample.budgetCurrency)}
              reasons={currentExample.reasons}
            />
            <div className="assistant-onboarding__example-actions">
              <button type="button" onClick={() => onDecision("fit")}>Да, моё</button>
              <button type="button" onClick={() => onDecision("not_fit")}>Нет, не моё</button>
            </div>
            {pendingNotFitId === currentExample.id ? (
              <div className="assistant-onboarding__reason-panel" role="group" aria-label="Почему заказ не подходит">
                <p>Почему не подходит?</p>
                <div className="assistant-onboarding__chip-grid">
                  {NOT_FIT_REASONS.map(([value, label]) => (
                    <button key={value} type="button" onClick={() => onDecision("not_fit", value)}>{label}</button>
                  ))}
                </div>
              </div>
            ) : null}
          </li>
        </ol>
      ) : null}
      {allExamplesDecided && examples.length ? (
        <p className="assistant-onboarding__hint">Готово: {decidedExamples} из {examples.length}. Сохраним ответы и включим поиск.</p>
      ) : null}
    </>
  );
}

function OnboardingProgress({ step }) {
  return (
    <div className="assistant-onboarding__progress" aria-label={`Этап ${step} из 2`}>
      <span className="type-meta">Этап {step} / 2</span>
      <ol>{STEPS.map((label, index) => <li key={label} className={index + 1 <= step ? "is-active" : ""}><span>{index + 1}</span><small>{label}</small></li>)}</ol>
    </div>
  );
}
